import { NodeId, useNode } from "@craftjs/core";
import cx from "classnames";
import { useRouter } from "next/router";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { buttonVariants } from "@/components/button";
import imageUploadPlaceholder from "@/components/editor/preference-panel/image-upload-placeholder.png";
import { CheckmarkCircle } from "@/components/icons/checkmark-circle";
import { ExclamationCircleIcon } from "@/components/icons/exclamation-circle";
import { ImageUploadProviderContext } from "@/components/image-upload-context/image-upload-provider-component";
import { ToastContent } from "@/components/toasts/toast-content-component";
import useAnalytics from "@/hooks/use-analytics";
import { IMAGE_USAGE_PROP_NAME } from "@/utils/constants";

import { Image } from "./image-upload-component-styles";

export type ImageUploadProps = {
  blockType: string;
  nodeId: NodeId;
  imagePropName: string;
  imageWidth: string;
  imageHeight: string;
  imageCornerRadius: "md" | "round";
};

export default function ImageUpload(ownProps: Partial<ImageUploadProps>) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const { logAnalytics } = useAnalytics();

  const {
    props,
    actions: { setProp },
  } = useNode((node) => {
    return {
      props: node.props,
      node,
    };
  });

  const imageSrc =
    props[ownProps.imagePropName as any] || imageUploadPlaceholder.src;

  const { query } = useRouter();
  const slug = query.slug as string;
  const { isUploading, uploadImage } = React.useContext(
    ImageUploadProviderContext
  );

  const handleUpload = React.useCallback(
    async (event) => {
      if (event.target.files.length > 0) {
        const file = event.target.files[0];
        inputRef.current.value = "";
        const sizeMb = file.size / 1024 / 1024;
        if (sizeMb > 10) {
          toast.error(
            <ToastContent
              icon={ExclamationCircleIcon}
              text={t("preference-panel.image-upload.file-size-error", {
                sizeMb: sizeMb.toFixed(2),
              })}
            />
          );
          return;
        }

        const { uploadResponse, imageUrl, imageUsageId } = await uploadImage(
          slug,
          file
        );

        if (uploadResponse?.status === 201) {
          setProp((props) => {
            props[ownProps.imagePropName] = imageUrl;

            if (imageUsageId) {
              props[IMAGE_USAGE_PROP_NAME] = imageUsageId;
            }
          });

          toast.success(
            <ToastContent
              icon={CheckmarkCircle}
              text={t("preference-panel.image-upload.image-uploaded")}
            />
          );

          logAnalytics("CANVAS_IMAGE_UPLOADED", {
            block_type: ownProps.blockType,
          });
        }
      }
    },
    [
      uploadImage,
      slug,
      setProp,
      t,
      logAnalytics,
      ownProps.blockType,
      ownProps.imagePropName,
    ]
  );

  return (
    <div className="grid gap-6">
      <Image
        src={imageSrc}
        alt="Placeholder for uploaded Header background"
        data-testid="image-upload-preview"
        width={ownProps.imageWidth}
        height={ownProps.imageHeight}
        className={cx("aspect-square", {
          "rounded-full": ownProps.imageCornerRadius === "round",
          "rounded-md": ownProps.imageCornerRadius !== "round",
        })}
      />
      <div className="flex">
        <label
          className={buttonVariants({
            size: "small",
            isInline: true,
            pattern: "primary",
            variant: "outlined",
            className:
              "cursor-pointer [&_input[type='file']]:hidden  border-solid min-w-[6.25rem]",
          })}
          data-testid="image-upload-button"
        >
          <input
            ref={inputRef}
            id="header-image-upload"
            data-testid="image-upload-input"
            name="header-image-upload"
            type="file"
            accept=".jpg,.png,.gif"
            disabled={isUploading}
            aria-disabled={isUploading}
            onChange={handleUpload}
          />
          {isUploading
            ? `${t("preference-panel.uploading")}…`
            : t("preference-panel.change")}
        </label>
      </div>
    </div>
  );
}
